import { Injectable, inject } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { QueryOptions } from 'odata-query';
import { map, Observable, shareReplay } from 'rxjs';
import {
  GstarClient,
  SecurityTypeResponseAuditableODataWithMetadataResponse,
} from '../../../web-api-client';
import {
  mapSecurityTypeFromResponse,
  mapSecurityTypeToCreateRequest,
  mapSecurityTypeToUpdateRequest,
} from '../mappers';

import { Collection, SecurityType } from '../models';

@Injectable({
  providedIn: 'root',
})
export class SecurityTypeService {
  private readonly gstarClient = inject(GstarClient);

  private cache$!: Observable<Collection<SecurityType>> | null;

  activeSecurityTypeList(): Observable<Collection<SecurityType>> {
    if (!this.cache$) {
      this.cache$ = this.gstarClient
        .securitytypesGET(
          undefined,
          'isActive eq true',
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        )
        .pipe(
          map(
            (
              entityResponse: SecurityTypeResponseAuditableODataWithMetadataResponse
            ) => ({
              total: entityResponse.odataCount ?? 0,
              items:
                entityResponse.value?.map(entity =>
                  mapSecurityTypeFromResponse(entity)
                ) ?? [],
            })
          ),
          shareReplay(1)
        );
    }

    return this.cache$;
  }

  get(
    query?: Partial<QueryOptions<SecurityType>>
  ): Observable<Collection<SecurityType>> {
    return this.gstarClient
      .securitytypesGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(responseEntity =>
              mapSecurityTypeFromResponse(responseEntity)
            ) ?? [],
        }))
      );
  }

  getById(clientId: number): Observable<SecurityType> {
    return this.gstarClient
      .clientsGET2(clientId)
      .pipe(map(response => mapSecurityTypeFromResponse(response)));
  }

  create(securityType: SecurityType): Observable<SecurityType> {
    return this.gstarClient
      .securitytypesPOST(mapSecurityTypeToCreateRequest(securityType))
      .pipe(map(response => mapSecurityTypeFromResponse(response)));
  }

  update(securityType: SecurityType): Observable<void> {
    if (securityType.parentSecurityTypeId === 0) {
      securityType.parentSecurityTypeId = undefined;
    }
    return this.gstarClient.securitytypesPUT(
      securityType.securityTypeId,
      mapSecurityTypeToUpdateRequest(securityType)
    );
  }

  clearCache(): void {
    this.cache$ = null;
  }
}
